import { getLocations, Location } from './api'; // Adjust import path as needed

// Module-level cache
let locationsCache: Location[] | null = null;
let isFetching = false;
let pendingPromise: Promise<Location[]> | null = null;

/**
 * Fetches locations from the API with caching
 * 
 * This function will:
 * 1. Return cached data if available
 * 2. Avoid duplicate API calls if one is already in progress
 * 3. Cache the results after fetching
 */
export const getCachedLocations = async (): Promise<Location[]> => {
  // If we already have cached data, return it immediately
  if (locationsCache !== null) {
    return locationsCache;
  }
  
  // If we're already fetching, return the pending promise to avoid duplicate requests
  if (isFetching && pendingPromise) {
    return pendingPromise;
  }
  
  // Set fetching flag and create a new promise
  isFetching = true;
  
  try {
    // Create a new promise for the fetch operation
    pendingPromise = getLocations().then(locations => {
      // Store in cache
      locationsCache = locations;
      return locations;
    }).finally(() => {
      // Reset fetching state when done
      isFetching = false;
    });
    
    // Return the promise
    return pendingPromise;
  } catch (error) {
    // Reset fetching state on error
    isFetching = false;
    throw error;
  }
};

/**
 * Manually clear the locations cache
 * This could be used if we later add a refresh button
 */
export const clearLocationsCache = (): void => {
  locationsCache = null;
};